import React, { useState, useMemo } from 'react';
import { Loader2, TrendingUp, Phone, Target, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { useCallIntentPatterns } from '../hooks/useReports';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import QueryInfoButton from '../components/QueryInfoButton';
import { CALL_CENTER_QUERIES } from '../constants/queries';

const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#14B8A6', '#A855F7', '#22C55E', '#E11D48', '#0EA5E9',
  '#D946EF', '#64748B', '#FB923C', '#4ADE80', '#F472B6'
];

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num?.toLocaleString() || '0';
}

export default function CallIntentPatterns() {
  const { data: patternsData, isLoading, isError } = useCallIntentPatterns();
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allGroups = new Set(groupedData.map((g) => g.primary_intent_a));
    setExpandedGroups(allGroups);
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  // Group patterns by primary_intent_a
  const groupedData = useMemo(() => {
    if (!patternsData?.data) return [];

    const groups = {};
    patternsData.data.forEach((pattern) => {
      const intentA = pattern.primary_intent_a || 'Unknown';
      if (!groups[intentA]) {
        groups[intentA] = {
          primary_intent_a: intentA,
          total_count: 0,
          children: [],
        };
      }
      groups[intentA].total_count += parseInt(pattern.call_count || 0);
      groups[intentA].children.push({
        primary_intent_b: pattern.primary_intent_b || 'Unknown',
        call_count: parseInt(pattern.call_count || 0),
        percentage: parseFloat(pattern.percentage || 0),
      });
    });

    // Sort groups by total count descending
    return Object.values(groups).sort((a, b) => b.total_count - a.total_count);
  }, [patternsData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin" />
          <p className="text-gray-500 text-lg">Analyzing call intent patterns...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 text-red-500">
          <AlertCircle className="w-12 h-12" />
          <p className="text-lg">Failed to load call intent patterns</p>
        </div>
      </div>
    );
  }

  const patterns = patternsData?.data || [];
  const totalCalls = patterns.reduce((sum, p) => sum + parseInt(p.call_count || 0), 0);

  const chartData = groupedData.map((g, index) => ({
    name: g.primary_intent_a,
    calls: g.total_count,
    percentage: ((g.total_count / totalCalls) * 100).toFixed(2),
    color: COLORS[index % COLORS.length],
  }));

  const pieData = groupedData.slice(0, 10).map((g, index) => ({
    name: g.primary_intent_a,
    value: g.total_count,
    color: COLORS[index % COLORS.length],
  }));

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Target className="w-7 h-7 text-primary-600" />
            Call Intent Patterns
          </h2>
          <p className="text-gray-500 mt-1">
            Top 20 most frequent call reasons based on primary_intent_a and primary_intent_b
          </p>
        </div>
        <QueryInfoButton
          title="Call Intent Data Sources"
          queries={[CALL_CENTER_QUERIES.intentPatterns]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <Phone className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Calls Analyzed</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(totalCalls)}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Unique Primary Intent A</p>
              <p className="text-2xl font-bold text-gray-900">{groupedData.length}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Target className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Top Intent A Coverage</p>
              <p className="text-2xl font-bold text-gray-900">
                {groupedData.length > 0 ? ((groupedData[0].total_count / totalCalls) * 100).toFixed(1) : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Primary Intent A by Volume</h3>
          <div className="h-[500px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={formatNumber} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={150}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => value?.length > 20 ? value.substring(0, 20) + '...' : value}
                />
                <Tooltip
                  formatter={(value, name, props) => [
                    `${formatNumber(value)} calls (${props.payload.percentage}%)`,
                    'Volume'
                  ]}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                />
                <Bar dataKey="calls" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Primary Intent A Distribution (Top 10)</h3>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name?.substring(0, 15)}${name?.length > 15 ? '...' : ''} (${(percent * 100).toFixed(1)}%)`}
                  labelLine={true}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [formatNumber(value), 'Calls']}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Grouped Intent Breakdown</h3>
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-1.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Collapse All
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Primary Intent A
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Primary Intent B
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Call Count
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Percentage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Distribution
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {groupedData.map((group, groupIndex) => {
                const isExpanded = expandedGroups.has(group.primary_intent_a);
                const groupPercentage = ((group.total_count / totalCalls) * 100).toFixed(2);

                return (
                  <React.Fragment key={group.primary_intent_a}>
                    {/* Parent Row - Primary Intent A */}
                    <tr
                      className="bg-gray-50 hover:bg-gray-100 cursor-pointer"
                      onClick={() => toggleGroup(group.primary_intent_a)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button className="p-1 hover:bg-gray-200 rounded">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-600" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-600" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-medium ${
                            groupIndex < 3 ? 'bg-primary-100 text-primary-700' : 'bg-gray-200 text-gray-600'
                          }`}>
                            {groupIndex + 1}
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {group.primary_intent_a}
                          </span>
                          <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                            {group.children.length} sub-intents
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 italic">
                        —
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-bold">
                        {group.total_count.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-bold">
                        {groupPercentage}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-32 bg-gray-300 rounded-full h-3">
                          <div
                            className="h-3 rounded-full"
                            style={{
                              width: `${Math.min(parseFloat(groupPercentage) * 2, 100)}%`,
                              backgroundColor: COLORS[groupIndex % COLORS.length]
                            }}
                          />
                        </div>
                      </td>
                    </tr>

                    {/* Child Rows - Primary Intent B */}
                    {isExpanded && group.children.map((child, childIndex) => (
                      <tr key={`${group.primary_intent_a}-${child.primary_intent_b}`} className="hover:bg-amber-50 bg-white">
                        <td className="px-6 py-3 whitespace-nowrap">
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap pl-16">
                          <span className="text-sm text-gray-400">└</span>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                          {child.primary_intent_b}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                          {child.call_count.toLocaleString()}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                          {child.percentage}%
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full opacity-70"
                              style={{
                                width: `${Math.min(child.percentage * 2, 100)}%`,
                                backgroundColor: COLORS[groupIndex % COLORS.length]
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
